"use client";

import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { sources, type Source } from "./sources";
import {
	IconChevronLeft,
	IconChevronRight,
	IconTarget,
	IconHeart,
	IconUsers,
	IconSchool,
	IconHome,
	IconWorld,
	IconHandStop,
} from "@/lib/tabler-icons";
import Link from "next/link";
import { SectionHeading, Kicker } from "@/components/ui/editorial";

/**
 * Icono específico para cada ámbito
 */
const getSourceIcon = (sourceId: number) => {
	const icons = {
		1: IconHeart, // Salud Mental
		2: IconUsers, // Migración
		3: IconSchool, // Educación Sexual
		4: IconHome, // Sin Hogar
		5: IconWorld, // Medio Ambiente
		6: IconHandStop, // Diversidad
		7: IconTarget, // Formación
		8: IconUsers, // Familia
		9: IconSchool, // Mayores
	};
	return icons[sourceId as keyof typeof icons] || IconTarget;
};

/**
 * Sección de ámbitos de actuación con carrusel editorial
 */
const SourcesSection = () => {
	// Embla Carousel setup con scroll suave
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: "start",
		loop: true,
		skipSnaps: false,
		dragFree: false,
		containScroll: "keepSnaps",
		duration: 20,
		breakpoints: {
			"(min-width: 640px)": { slidesToScroll: 1 },
			"(min-width: 768px)": { slidesToScroll: 1 },
			"(min-width: 1024px)": { slidesToScroll: 1 },
		},
	});

	// Autoplay setup
	const [isPlaying, setIsPlaying] = useState(true);
	const autoplayInterval = 5000;

	const autoplay = useCallback(() => {
		if (!emblaApi || !isPlaying) return;
		emblaApi.scrollNext();
	}, [emblaApi, isPlaying]);

	useEffect(() => {
		const interval = setInterval(autoplay, autoplayInterval);
		return () => clearInterval(interval);
	}, [autoplay]);

	// Navigation handlers
	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	// Progress indicator
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		// Patrón oficial de Embla: al inicializarse la API se sincronizan los
		// puntos de scroll y el índice activo, y se suscribe al evento "select".
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setScrollSnaps(emblaApi.scrollSnapList());
		emblaApi.on("select", onSelect);
		onSelect();
		return () => {
			emblaApi.off("select", onSelect);
		};
	}, [emblaApi, onSelect]);

	return (
		<section
			id="ambitos"
			className="px-4 xl:px-0 py-16"
			aria-labelledby="ambitos-title"
			role="region"
		>
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header editorial */}
				<header>
					<SectionHeading
						index="05"
						title="Ámbitos de Actuación"
						kicker="Nuestros Ámbitos"
					/>
					<p
						id="ambitos-title"
						className="font-body text-lg mt-4 max-w-3xl leading-relaxed text-muted-foreground"
					>
						¿Dónde intervenimos y cocreamos proyectos? Descubre los contextos
						clave donde acompañamos a las personas y comunidades.
					</p>
				</header>

				{/* Carrusel editorial */}
				<div className="relative">
					{/* Navigation Buttons */}
					<div className="absolute -left-4 -right-4 top-1/2 -translate-y-1/2 z-10 flex justify-between pointer-events-none">
						<button
							onClick={scrollPrev}
							className="p-3 rounded-full bg-cream border border-border hover:bg-ink hover:text-cream transition-all duration-200 pointer-events-auto focus:outline-none focus:ring-2 focus:ring-ink focus:ring-offset-2 cursor-pointer"
							aria-label="Ver ámbito anterior"
						>
							<IconChevronLeft className="w-5 h-5" />
						</button>
						<button
							onClick={scrollNext}
							className="p-3 rounded-full bg-cream border border-border hover:bg-ink hover:text-cream transition-all duration-200 pointer-events-auto focus:outline-none focus:ring-2 focus:ring-ink focus:ring-offset-2 cursor-pointer"
							aria-label="Ver siguiente ámbito"
						>
							<IconChevronRight className="w-5 h-5" />
						</button>
					</div>

					{/* Carousel Container */}
					<div
						className="overflow-hidden cursor-grab active:cursor-grabbing py-2"
						ref={emblaRef}
						onMouseEnter={() => setIsPlaying(false)}
						onMouseLeave={() => setIsPlaying(true)}
						role="region"
						aria-label="Carrusel de ámbitos de actuación"
					>
						<div className="flex">
							{sources.map((source: Source) => {
								const SourceIcon = getSourceIcon(source.id);
								return (
									<div
										key={source.id}
										className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
									>
										<article
											className="relative flex flex-col p-3"
											role="article"
											aria-labelledby={`source-${source.id}-title`}
										>
											{/* Card editorial */}
											<div className="group border border-border rounded-xl overflow-hidden h-full">
												{/* Header de la card */}
												<div className="p-5 border-b border-border flex items-start gap-3">
													<div className="p-2 border border-border rounded-lg shrink-0">
														<SourceIcon className="w-5 h-5 text-terracotta" />
													</div>
													<div className="min-w-0">
														<span className="font-display text-xs font-semibold text-terracotta uppercase tracking-wide block mb-1">
															Ámbito {source.id}
														</span>
														<h3
															id={`source-${source.id}-title`}
															className="font-display text-lg font-semibold text-ink leading-tight"
														>
															{source.name}
														</h3>
													</div>
												</div>

												{/* Imagen */}
												<div className="relative aspect-[4/3] overflow-hidden">
													<Image
														src={source.image}
														alt={`Imagen representativa del ámbito: ${source.name}`}
														fill
														sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
														className="object-cover transition-transform duration-700 group-hover:scale-105"
														loading="lazy"
													/>
													{/* Description overlay on hover */}
													<div className="absolute inset-0 bg-ink/80 opacity-0 group-hover:opacity-100 transition-all duration-400 flex items-center justify-center p-6">
														<p className="text-cream font-body text-sm text-center leading-relaxed">
															{source.description}
														</p>
													</div>
												</div>

												{/* Footer de la card */}
												<div className="p-5">
													<p className="font-body text-muted-foreground text-sm leading-relaxed line-clamp-2">
														{source.description}
													</p>
												</div>
											</div>
										</article>
									</div>
								);
							})}
						</div>
					</div>

					{/* Progress Indicator editorial */}
					{/*
					 * El punto visible sigue midiendo 8px, pero el <button> que lo
					 * envuelve mide 24px de alto y ancho mínimo (px-2 + h-6) para
					 * cumplir el tamaño de objetivo táctil de WCAG 2.2 (SC 2.5.8).
					 * Los botones van pegados (sin gap ni margen negativo): si se
					 * solapan, axe los considera parcialmente ocultos y vuelve a
					 * fallar. El precio es que los puntos quedan a 16px en vez de 8px.
					 */}
					<div
						className="flex justify-center mt-6"
						role="tablist"
						aria-label="Navegación de ámbitos de actuación"
					>
						{scrollSnaps.map((_, index) => (
							<button
								key={index}
								onClick={() => emblaApi?.scrollTo(index)}
								className="group flex h-6 items-center justify-center px-2 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-ink focus:ring-offset-2"
								aria-label={`Ir al ámbito ${index + 1}`}
								aria-selected={index === selectedIndex}
								role="tab"
							>
								<span
									className={`block h-2 rounded-full transition-all duration-300 ${
										index === selectedIndex
											? "bg-terracotta w-8"
											: "bg-border group-hover:bg-ink/30 w-2"
									}`}
								/>
							</button>
						))}
					</div>
				</div>

				{/* Footer editorial */}
				<div className="pt-8 text-center border-t border-border">
					<Kicker>Identifica tu Pasión</Kicker>
					<h3 className="font-display text-2xl md:text-3xl font-semibold text-ink mt-2 mb-4">
						¿Te identificas con algún ámbito?
					</h3>
					<p className="font-body text-muted-foreground text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
						Existen infinidad de propuestas. Simplemente observa, valora las
						necesidades y ACTÚA. Cada ámbito es una oportunidad para crear
						cambio positivo en nuestra comunidad.
					</p>
					<Link
						href="#contacto"
						className="font-body inline-block bg-ink text-cream px-8 py-3 rounded-full font-medium hover:bg-ink/90 transition-colors duration-200"
					>
						Colaborar
					</Link>
				</div>
			</div>
		</section>
	);
};

export default SourcesSection;
